/**
 * Firebase configuration
 */
import { initializeApp, getApps } from 'firebase/app';
/**
 * Get environment variable
 * In Vite projects, use VITE_ prefixed variables via import.meta.env
 * In Node.js, use regular process.env variables
 * Safe for Electron renderer process
 */
function getEnv(key, fallback = '') {
    try {
        // Try import.meta.env first (Vite projects - works in browser and Electron renderer)
        // Note: import.meta is a Vite-specific feature, so we need to check carefully
        try {
            // Use dynamic property access to avoid TypeScript errors
            const importMeta = globalThis.import?.meta || window.import?.meta;
            if (importMeta && importMeta.env) {
                const viteKey = `VITE_${key}`;
                const value = importMeta.env[viteKey] || importMeta.env[key];
                if (value && typeof value === 'string' && value.trim() !== '') {
                    return value;
                }
            }
        }
        catch (error) {
            // import.meta.env might not be available in all contexts
        }
        // Fallback to process.env (Node.js/Electron main process)
        if (typeof process !== 'undefined' && process.env) {
            try {
                const viteKey = `VITE_${key}`;
                const value = process.env[viteKey] || process.env[key];
                if (value && typeof value === 'string' && value.trim() !== '') {
                    return value;
                }
            }
            catch (error) {
                // Silently fail in Electron renderer to prevent crashes
                // process.env access can cause access violations in Electron renderer
            }
        }
    }
    catch (error) {
        // Outer try-catch for any unexpected errors
        console.warn(`Environment variable access failed for ${key}:`, error);
    }
    return fallback;
}
/**
 * Get Firebase configuration from environment
 *
 * Expected environment variables (Vite projects):
 * - VITE_FIREBASE_PROJECT_ID
 * - VITE_FIREBASE_API_KEY
 * - VITE_FIREBASE_AUTH_DOMAIN
 * - VITE_FIREBASE_STORAGE_BUCKET
 * - VITE_FIREBASE_APP_ID
 * - VITE_USE_FIREBASE_EMULATOR (optional)
 *
 * Falls back to non-VITE_ prefixed vars for Node.js environments
 */
export function getFirebaseConfig() {
    const projectId = getEnv('FIREBASE_PROJECT_ID', 'voxapilot-14e46');
    const apiKey = getEnv('FIREBASE_API_KEY', '');
    const authDomain = getEnv('FIREBASE_AUTH_DOMAIN', `${projectId}.firebaseapp.com`);
    const storageBucket = getEnv('FIREBASE_STORAGE_BUCKET', `${projectId}.appspot.com`);
    const useEmulator = getEnv('USE_FIREBASE_EMULATOR', 'false') === 'true' ||
        getEnv('VITE_USE_FIREBASE_EMULATOR', 'false') === 'true';
    // Log config status (without exposing sensitive data)
    if (typeof window !== 'undefined') {
        console.log('[Shared Firebase Config]', {
            projectId,
            apiKeyPresent: !!apiKey && apiKey !== '',
            apiKeyLength: apiKey ? apiKey.length : 0,
            authDomain,
            useEmulator,
        });
    }
    // Validate API key
    if (!useEmulator && (!apiKey || apiKey.trim() === '')) {
        const errorMsg = 'Firebase API key is missing. Set VITE_FIREBASE_API_KEY in .env.local';
        console.error('[Shared Firebase Config]', errorMsg);
        // Don't throw - let Firebase throw a clearer error
    }
    return {
        apiKey: apiKey || 'MISSING_API_KEY',
        authDomain,
        projectId,
        storageBucket,
        // Only include these if not using emulator
        ...(useEmulator ? {} : {
            appId: getEnv('FIREBASE_APP_ID', ''),
            measurementId: getEnv('FIREBASE_MEASUREMENT_ID', '') || undefined,
        }),
    };
}
/**
 * Initialize Firebase app
 * IMPORTANT: This should only be called if no Firebase app exists yet.
 * The frontend config should initialize Firebase first with correct env vars.
 */
export function initializeFirebase() {
    const apps = getApps();
    if (apps.length > 0) {
        console.log('[Shared Firebase Config] ✅ Reusing existing Firebase app');
        return apps[0];
    }
    // No app exists - try to initialize
    // WARNING: This will likely fail in Electron because env vars aren't accessible
    console.warn('[Shared Firebase Config] ⚠️ No Firebase app found, attempting to initialize...');
    console.warn('[Shared Firebase Config] ⚠️ This may fail if env vars are not accessible');
    const config = getFirebaseConfig();
    // Log config before initializing (without exposing sensitive data)
    if (typeof window !== 'undefined') {
        console.log('[Shared Firebase Config] Config being used:', {
            apiKeyPresent: !!config.apiKey && config.apiKey !== '' && config.apiKey !== 'MISSING_API_KEY',
            apiKeyLength: config.apiKey ? config.apiKey.length : 0,
            projectId: config.projectId,
            authDomain: config.authDomain,
        });
    }
    // If API key is missing, throw error instead of initializing with invalid config
    if (!config.apiKey || config.apiKey === '' || config.apiKey === 'MISSING_API_KEY') {
        const error = new Error('Firebase API key is missing. The frontend config should initialize Firebase first. ' +
            'Make sure packages/frontend/src/config/firebase.ts initializes Firebase before the shared package tries to use it.');
        console.error('[Shared Firebase Config] ❌', error.message);
        throw error;
    }
    const app = initializeApp(config);
    // Connect to emulators if enabled
    const useEmulator = getEnv('USE_FIREBASE_EMULATOR', 'false') === 'true' ||
        getEnv('VITE_USE_FIREBASE_EMULATOR', 'false') === 'true';
    if (useEmulator && typeof window !== 'undefined') {
        // Connect Auth to emulator
        const { connectAuthEmulator, getAuth } = require('firebase/auth');
        const auth = getAuth(app);
        if (!auth._delegate._config?.emulator) {
            connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        }
        // Connect Firestore to emulator
        const { connectFirestoreEmulator, getFirestore } = require('firebase/firestore');
        const db = getFirestore(app);
        if (!db._delegate._settings?.host?.includes('localhost')) {
            connectFirestoreEmulator(db, 'localhost', 8080);
        }
        // Connect Storage to emulator
        const { connectStorageEmulator, getStorage } = require('firebase/storage');
        const storage = getStorage(app);
        if (!storage._delegate._host?.includes('localhost')) {
            connectStorageEmulator(storage, 'localhost', 9199);
        }
    }
    return app;
}
/**
 * Get API base URL
 *
 * For emulator: http://localhost:5001/{projectId}/us-central1
 * For production: https://{region}-{projectId}.cloudfunctions.net
 */
export function getApiBaseUrl() {
    const useEmulator = getEnv('USE_FIREBASE_EMULATOR', 'false') === 'true';
    const projectId = getEnv('FIREBASE_PROJECT_ID', 'voxapilot-14e46');
    if (useEmulator) {
        return `http://localhost:5001/${projectId}/us-central1`;
    }
    const region = getEnv('FIREBASE_REGION', 'us-central1');
    return `https://${region}-${projectId}.cloudfunctions.net`;
}
//# sourceMappingURL=config.js.map